var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { ButtonLabelIcon } from './parts/ButtonLabelIcon';
import { ButtonLabel } from './parts/ButtonLabel';
import { createComponent, mouseFocusBehavior, focusRing, styled, } from '@workday/canvas-kit-react/common';
import { boxStyleFn } from '@workday/canvas-kit-react/layout';
import { borderRadius, space, spaceNumbers, type } from '@workday/canvas-kit-react/tokens';
function getIconColorSelectors(_a, color, fill) {
    var themePrimary = _a.canvas.palette.primary;
    return {
        '&:focus span, &:hover span, & span': {
            '.wd-icon-fill, .wd-icon-accent, .wd-icon-accent2': {
                fill: color,
            },
            '.wd-icon-background': {
                fill: fill ? color : undefined,
            },
            '.wd-icon-background ~ .wd-icon-accent, .wd-icon-background ~ .wd-icon-accent2': {
                fill: fill
                    ? color === themePrimary.contrast
                        ? themePrimary.main
                        : themePrimary.contrast
                    : color,
            },
        },
    };
}
var ButtonContainer = styled('button')(__assign(__assign({}, type.levels.subtext.large), { cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', boxShadow: 'none', outline: 'none', fontWeight: type.properties.fontWeights.bold, lineHeight: 'normal', whiteSpace: 'nowrap', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', borderRadius: borderRadius.circle, position: 'relative', verticalAlign: 'middle', overflow: 'hidden', border: '1px solid transparent', background: 'transparent', transition: 'box-shadow 120ms linear, border 120ms linear, background-color 120ms linear, color 120ms linear', '&:disabled, &:disabled:active': {
        cursor: 'default',
        boxShadow: 'none',
        opacity: 0.4,
    }, '&:hover:active': { transitionDuration: '40ms' }, '& > *:first-of-type': {
        marginLeft: '0',
    }, '& > *:last-of-type': {
        marginRight: 0,
    } }), function (_a) {
    var size = _a.size;
    switch (size) {
        case 'large':
            return __assign(__assign({}, type.levels.body.small), { fontWeight: type.properties.fontWeights.bold, height: '48px', '& > * ': {
                    margin: "0 " + spaceNumbers.xxs / 2 + "px",
                } });
        case 'medium':
        default:
            return {
                height: space.xl,
                '& > * ': {
                    margin: "0 " + spaceNumbers.xxs / 2 + "px",
                },
            };
        case 'small':
            return {
                height: space.l,
                '& > * ': {
                    margin: "0 " + spaceNumbers.xxxs / 2 + "px",
                },
            };
        case 'extraSmall':
            return __assign(__assign({}, type.levels.subtext.medium), { fontWeight: type.properties.fontWeights.bold, height: space.m, '& > * ': {
                    margin: "0 " + spaceNumbers.xxxs / 2 + "px",
                } });
    }
}, function (_a) {
    var grow = _a.grow;
    return grow && { width: '100%', maxWidth: '100%' };
}, function (_a) {
    var colors = _a.colors, fillIcon = _a.fillIcon, theme = _a.theme;
    if (!colors) {
        return;
    }
    var baseStyles = __assign({ backgroundColor: colors.default.background, borderColor: colors.default.border, color: colors.default.label }, (colors.default.icon && __assign({ '.wd-icon-fill, .wd-icon-accent, .wd-icon-accent2, .wd-icon-background': {
            transition: 'fill 120ms ease-in',
        } }, getIconColorSelectors(theme, colors.default.icon, fillIcon))));
    var hoverStyles = {
        '&:hover': __assign({ backgroundColor: colors.hover.background, borderColor: colors.hover.border, color: colors.hover.label }, (colors.hover.icon && getIconColorSelectors(theme, colors.hover.icon, fillIcon))),
    };
    var activeStyles = {
        '&:active, &:focus:active, &:hover:active': __assign({ backgroundColor: colors.active.background, borderColor: colors.active.border, color: colors.active.label }, (colors.active.icon && getIconColorSelectors(theme, colors.active.icon, fillIcon))),
    };
    return __assign(__assign(__assign(__assign(__assign(__assign({}, baseStyles), { '&:focus': __assign(__assign({ backgroundColor: colors.focus.background, borderColor: colors.focus.border, color: colors.focus.label }, (colors.focus.focusRing || focusRing({ separation: 2 }, theme))), (colors.focus.icon && getIconColorSelectors(theme, colors.focus.icon, fillIcon))) }), activeStyles), hoverStyles), { '&:disabled, &:active:disabled, &:focus:disabled, &:hover:disabled': __assign({ backgroundColor: colors.disabled.background, borderColor: colors.disabled.border, color: colors.disabled.label, opacity: colors.disabled.opacity }, (colors.disabled.icon && getIconColorSelectors(theme, colors.disabled.icon, fillIcon))) }), mouseFocusBehavior({
        '&:focus': __assign(__assign(__assign(__assign({}, baseStyles), { outline: 'none', boxShadow: 'none', animation: 'none' }), hoverStyles), activeStyles),
    }));
}, boxStyleFn);
export var getMinWidthStyles = function (children, size) {
    switch (size) {
        case 'large':
            return children ? '112px' : '48px';
        case 'medium':
            return children ? '96px' : space.xl;
        case 'small':
            return children ? space.xxxl : space.l;
        case 'extraSmall':
            return children ? 'auto' : space.m;
        default:
            return children ? '96px' : space.xl;
    }
};
export var getPaddingStyles = function (children, size, icon, iconPosition) {
    // In order to calculate the correct padding, we need to know its children
    // and what side the icon is on and if there's an icon provided
    if (!children) {
        // icon buttons do not have any padding
        return 0;
    }
    // If there are children AND an icon
    // 1. We check what side the icon is in
    // 2. Adjust padding to visually center the icon and text
    // If there is children (most likely just text)
    // 1. We keep the padding the same on both side
    switch (size) {
        case 'large':
            return icon
                ? iconPosition === 'start'
                    ? "0 " + space.l + " 0 " + space.m
                    : "0 " + space.m + " 0 " + space.l
                : "0 " + space.l;
        case 'medium':
            return icon
                ? iconPosition === 'start'
                    ? "0 " + space.m + " 0 20px"
                    : "0 20px 0 " + space.m
                : "0 " + space.m;
        case 'small':
            return icon
                ? iconPosition === 'start'
                    ? "0 " + space.s + " 0 " + space.xs
                    : "0 " + space.xs + " 0 " + space.s
                : "0 " + space.s;
        case 'extraSmall':
            return icon
                ? iconPosition === 'start'
                    ? "0 " + space.xs + " 0 " + space.xxs
                    : "0 " + space.xxs + " 0 " + space.xs
                : "0 " + space.xs;
        default:
            return icon
                ? iconPosition === 'start'
                    ? "0 " + space.m + " 0 20px"
                    : "0 20px 0 " + space.m
                : "0 " + space.m;
    }
};
export var BaseButton = createComponent('button')({
    displayName: 'Button',
    Component: function (_a, ref, Element) {
        var children = _a.children, elemProps = __rest(_a, ["children"]);
        return (React.createElement(ButtonContainer, __assign({ as: Element, ref: ref }, elemProps), children));
    },
    subComponents: {
        Icon: ButtonLabelIcon,
        Label: ButtonLabel,
    },
});
